export enum LoanType {
  Annuity = 'annuity',
  Balloon = 'balloon',
}

export interface LoanInput {
  amount: number
  months: number
  interestRate: number
}

export interface AnnuityResult {
  monthlyPayment: number
  totalPayment: number
  totalInterest: number
}

export interface BalloonResult {
  monthlyPayment: number
  totalPayment: number
  totalInterest: number
  finalPayment: number
}

export const calculateAnnuity = (
  amount: number,
  months: number,
  interestRate: number
): AnnuityResult | null => {
  if (
    !amount ||
    !months ||
    interestRate === null ||
    interestRate === undefined
  ) {
    return null
  }

  const monthlyRate = interestRate / 12 / 100
  let monthlyPayment: number

  if (monthlyRate === 0) {
    monthlyPayment = amount / months
  } else {
    const power = Math.pow(1 + monthlyRate, months)
    monthlyPayment = (amount * (monthlyRate * power)) / (power - 1)
  }

  const totalPayment = monthlyPayment * months
  const totalInterest = totalPayment - amount

  return {
    monthlyPayment: Math.round(monthlyPayment),
    totalPayment: Math.round(totalPayment),
    totalInterest: Math.round(totalInterest),
  }
}

export const calculateBalloon = (
  amount: number,
  months: number,
  interestRate: number
): BalloonResult | null => {
  if (
    !amount ||
    !months ||
    interestRate === null ||
    interestRate === undefined
  ) {
    return null
  }

  const monthlyRate = interestRate / 12 / 100
  const monthlyInterest = amount * monthlyRate
  const totalInterest = monthlyInterest * months
  const finalPayment = amount + monthlyInterest
  const totalPayment = monthlyInterest * (months - 1) + finalPayment

  return {
    monthlyPayment: Math.round(monthlyInterest),
    finalPayment: Math.round(finalPayment),
    totalPayment: Math.round(totalPayment),
    totalInterest: Math.round(totalInterest),
  }
}

export interface PaymentScheduleItem {
  month: number
  principal: number
  interest: number
  remainingBalance: number
  totalPayment: number
  cumulativePrincipal: number
  cumulativeInterest: number
  remainingPrincipal: number
  remainingInterest: number
}

export const generateAnnuitySchedule = (
  amount: number,
  months: number,
  interestRate: number
): PaymentScheduleItem[] => {
  if (
    !amount ||
    !months ||
    interestRate === null ||
    interestRate === undefined
  ) {
    return []
  }

  const monthlyRate = interestRate / 12 / 100
  let monthlyPayment: number

  if (monthlyRate === 0) {
    monthlyPayment = amount / months
  } else {
    const power = Math.pow(1 + monthlyRate, months)
    monthlyPayment = (amount * (monthlyRate * power)) / (power - 1)
  }

  const schedule: PaymentScheduleItem[] = []
  let remainingPrincipalBalance = amount
  let cumulativePrincipal = 0
  let cumulativeInterest = 0

  // Nejdříve spočítám celkové úroky
  const totalInterest = monthlyPayment * months - amount

  for (let month = 1; month <= months; month++) {
    const interestPayment = remainingPrincipalBalance * monthlyRate
    const principalPayment = monthlyPayment - interestPayment
    remainingPrincipalBalance -= principalPayment
    cumulativePrincipal += principalPayment
    cumulativeInterest += interestPayment

    const remainingPrincipal = Math.max(
      0,
      Math.round(amount - cumulativePrincipal)
    )
    const remainingInterest = Math.max(
      0,
      Math.round(totalInterest - cumulativeInterest)
    )
    const remainingBalance = remainingPrincipal + remainingInterest

    schedule.push({
      month,
      principal: Math.round(principalPayment),
      interest: Math.round(interestPayment),
      remainingBalance,
      totalPayment: Math.round(monthlyPayment),
      cumulativePrincipal: Math.round(cumulativePrincipal),
      cumulativeInterest: Math.round(cumulativeInterest),
      remainingPrincipal,
      remainingInterest,
    })
  }

  return schedule
}

export const generateBalloonSchedule = (
  amount: number,
  months: number,
  interestRate: number
): PaymentScheduleItem[] => {
  if (
    !amount ||
    !months ||
    interestRate === null ||
    interestRate === undefined
  ) {
    return []
  }

  const monthlyRate = interestRate / 12 / 100
  const monthlyInterest = amount * monthlyRate
  const totalInterest = monthlyInterest * months
  const schedule: PaymentScheduleItem[] = []
  let cumulativePrincipal = 0
  let cumulativeInterest = 0

  for (let month = 1; month <= months; month++) {
    const isLastMonth = month === months
    const principalPayment = isLastMonth ? amount : 0
    const interestPayment = monthlyInterest
    const totalPayment = principalPayment + interestPayment
    const remainingBalance = isLastMonth ? 0 : amount

    cumulativePrincipal += principalPayment
    cumulativeInterest += interestPayment

    schedule.push({
      month,
      principal: Math.round(principalPayment),
      interest: Math.round(interestPayment),
      remainingBalance: Math.round(remainingBalance),
      totalPayment: Math.round(totalPayment),
      cumulativePrincipal: Math.round(cumulativePrincipal),
      cumulativeInterest: Math.round(cumulativeInterest),
      remainingPrincipal: Math.round(amount - cumulativePrincipal),
      remainingInterest: Math.round(totalInterest - cumulativeInterest),
    })
  }

  return schedule
}
